import { 
  ref, 
  uploadBytesResumable, 
  deleteObject, 
  getDownloadURL, 
  listAll 
} from 'firebase/storage';
import { storage } from './firebase';

/**
 * Resizes an image file to fit within the specified size limit
 * @param {File} file - The image file to resize
 * @param {number} maxSizeBytes - Maximum size in bytes (default: 1.5MB)
 * @param {number} quality - JPEG quality 0-1 (default: 0.85)
 * @param {number} maxWidth - Maximum width in pixels (default: 2048)
 * @param {number} maxHeight - Maximum height in pixels (default: 1536)
 * @returns {Promise<File>} - Resized file
 */
export const resizeImageFile = async (file, maxSizeBytes = 1.5 * 1024 * 1024, quality = 0.85, maxWidth = 2048, maxHeight = 1536) => {
  return new Promise((resolve, reject) => {
    // Create canvas and context
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      // Keep original dimensions first to preserve quality
      // Only scale down if absolutely necessary for file size
      
      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;
      
      // Use better image rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      
      // Draw image
      ctx.drawImage(img, 0, 0, width, height);
      
      // Try highest quality first, then reduce only if needed
      const tryQualityLevel = (testQuality) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Error al procesar la imagen'));
            return;
          }
          
          // If size is acceptable, return it
          if (blob.size <= maxSizeBytes) {
            const resizedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(resizedFile);
            return;
          }
          
          // Try next lower quality level
          if (testQuality > 0.7) {
            tryQualityLevel(testQuality - 0.05);
          } else if (testQuality > 0.5) {
            tryQualityLevel(testQuality - 0.02);
          } else {
            // Only now try dimension reduction
            if (width > maxWidth || height > maxHeight) {
              const widthRatio = maxWidth / width;
              const heightRatio = maxHeight / height;
              const ratio = Math.min(widthRatio, heightRatio);
              
              const newWidth = Math.round(width * ratio);
              const newHeight = Math.round(height * ratio);
              
              canvas.width = newWidth;
              canvas.height = newHeight;
              ctx.drawImage(img, 0, 0, newWidth, newHeight);
              width = newWidth;
              height = newHeight;
              
              // Try again with reduced dimensions at good quality
              tryQualityLevel(0.85);
            } else {
              // Accept current result
              const resizedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(resizedFile);
            }
          }
        }, 'image/jpeg', testQuality);
      };
      
      // Start with highest quality and work down
      tryQualityLevel(0.95);
    };
    
    img.onerror = () => {
      reject(new Error('Error al cargar la imagen para redimensionar'));
    };
    
    // Load image
    img.src = URL.createObjectURL(file);
  });
};

// File validation constants
const SUPPORTED_FORMATS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'webm'];
const MAX_FILE_SIZE = 1.5 * 1024 * 1024; // 1.5MB in bytes
const MAX_VIDEO_FILE_SIZE = 3.2 * 1024 * 1024; // 3.2MB in bytes for video files

/**
 * Validates a file for photo upload
 * @param {File} file - The file to validate
 * @returns {Object} - { isValid: boolean, error?: string }
 */
export const validatePhotoFile = (file) => {
  // Check file format first
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  if (!fileExtension || !SUPPORTED_FORMATS.includes(fileExtension)) {
    return {
      isValid: false,
      error: `El archivo ${file.name} tiene un formato no soportado. Formatos permitidos: ${SUPPORTED_FORMATS.join(', ')}`
    };
  }

  // Check file size based on type
  const isVideoFile = fileExtension === 'webm';
  const maxSize = isVideoFile ? MAX_VIDEO_FILE_SIZE : MAX_FILE_SIZE;
  const maxSizeMB = isVideoFile ? '3.2MB' : '1.5MB';
  
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `El archivo ${file.name} excede el tamaño máximo de ${maxSizeMB}`
    };
  }

  return { isValid: true };
};

/**
 * Generates a unique photo filename with timestamp
 * @param {string} originalName - Original filename
 * @returns {string} - Formatted filename
 */
export const generatePhotoFileName = (originalName) => {
  const timestamp = Date.now();
  const sanitizedName = originalName.replace(/[^a-zA-Z0-9.-]/g, '_');
  return `photo_${timestamp}_${sanitizedName}`;
};

/**
 * Gets the storage path for equipment photos
 * @param {string} orderId - Order ID
 * @param {string} equipmentId - Equipment ID
 * @param {string} photoId - Photo filename (optional)
 * @returns {string} - Storage path
 */
export const getStoragePath = (orderId, equipmentId, photoId = '') => {
  const basePath = `work-orders/${orderId}/equipment/${equipmentId}/media`;
  return photoId ? `${basePath}/${photoId}` : basePath;
};

/**
 * Uploads multiple equipment photos with individual progress tracking
 * @param {string} orderId - Order ID
 * @param {string} equipmentId - Equipment ID
 * @param {FileList|File[]} files - Files to upload
 * @param {Function} onProgress - Progress callback (fileIndex, progress, snapshot)
 * @returns {Promise<Array>} - Array of upload results
 */
export const uploadEquipmentPhotos = async (orderId, equipmentId, files, onProgress) => {
  if (!orderId || !equipmentId) {
    throw new Error('orderId y equipmentId son requeridos');
  }

  if (!files || files.length === 0) {
    throw new Error('No se proporcionaron archivos para subir');
  }

  const filesArray = Array.from(files);
  const uploadPromises = filesArray.map(async (file, index) => {
    try {
      // Validate file
      const validation = validatePhotoFile(file);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Generate unique filename
      const photoId = generatePhotoFileName(file.name);
      const storagePath = getStoragePath(orderId, equipmentId, photoId);
      const storageRef = ref(storage, storagePath);

      // Create upload task
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            // Progress tracking
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (onProgress) {
              onProgress(index, progress, snapshot);
            }
          },
          (error) => {
            // Handle upload errors
            console.error(`Error uploading ${file.name}:`, error);
            reject(new Error(`Error al subir ${file.name}: ${error.message}`));
          },
          async () => {
            try {
              // Upload completed successfully
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve({
                success: true,
                photoId,
                filename: file.name,
                downloadURL,
                size: file.size,
                uploadedAt: new Date().toISOString()
              });
            } catch (error) {
              reject(new Error(`Error obteniendo URL de descarga para ${file.name}: ${error.message}`));
            }
          }
        );
      });
    } catch (error) {
      // Return error result for this file
      return {
        success: false,
        filename: file.name,
        error: error.message
      };
    }
  });

  try {
    const results = await Promise.allSettled(uploadPromises);
    
    return results.map((result, index) => {
      if (result.status === 'fulfilled') {
        return result.value;
      } else {
        return {
          success: false,
          filename: filesArray[index]?.name || `Archivo ${index + 1}`,
          error: result.reason?.message || 'Error desconocido durante la subida'
        };
      }
    });
  } catch (error) {
    throw new Error(`Error durante la subida de archivos: ${error.message}`);
  }
};

/**
 * Deletes a specific equipment photo
 * @param {string} orderId - Order ID
 * @param {string} equipmentId - Equipment ID
 * @param {string} photoId - Photo filename to delete
 * @returns {Promise<boolean>} - Success status
 */
export const deleteEquipmentPhoto = async (orderId, equipmentId, photoId) => {
  if (!orderId || !equipmentId || !photoId) {
    throw new Error('orderId, equipmentId, y photoId son requeridos');
  }

  try {
    const storagePath = getStoragePath(orderId, equipmentId, photoId);
    const photoRef = ref(storage, storagePath);
    
    await deleteObject(photoRef);
    return true;
  } catch (error) {
    // If file doesn't exist, consider it a success (already deleted)
    if (error.code === 'storage/object-not-found') {
      return true;
    }
    
    console.error(`Error deleting photo ${photoId}:`, error);
    throw new Error(`Error al eliminar la foto ${photoId}: ${error.message}`);
  }
};

/**
 * Deletes all photos for a specific equipment
 * @param {string} orderId - Order ID
 * @param {string} equipmentId - Equipment ID
 * @returns {Promise<Object>} - Deletion results
 */
export const deleteAllEquipmentPhotos = async (orderId, equipmentId) => {
  if (!orderId || !equipmentId) {
    throw new Error('orderId y equipmentId son requeridos');
  }

  try {
    const folderPath = getStoragePath(orderId, equipmentId);
    const folderRef = ref(storage, folderPath);
    
    // List all files in the folder
    const listResult = await listAll(folderRef);
    
    if (listResult.items.length === 0) {
      return {
        success: true,
        deletedCount: 0,
        message: 'No se encontraron fotos para eliminar'
      };
    }

    // Delete all files
    const deletePromises = listResult.items.map(async (itemRef) => {
      try {
        await deleteObject(itemRef);
        return { success: true, filename: itemRef.name };
      } catch (error) {
        console.error(`Error deleting ${itemRef.name}:`, error);
        return { success: false, filename: itemRef.name, error: error.message };
      }
    });

    const results = await Promise.allSettled(deletePromises);
    const deletedCount = results.filter(result => 
      result.status === 'fulfilled' && result.value.success
    ).length;

    return {
      success: true,
      deletedCount,
      totalFiles: listResult.items.length,
      results: results.map(result => 
        result.status === 'fulfilled' ? result.value : { success: false, error: result.reason }
      )
    };
  } catch (error) {
    console.error(`Error deleting all photos for equipment ${equipmentId}:`, error);
    throw new Error(`Error al eliminar todas las fotos del equipo: ${error.message}`);
  }
};

/**
 * Gets the download URL for a specific equipment photo
 * @param {string} orderId - Order ID
 * @param {string} equipmentId - Equipment ID
 * @param {string} photoId - Photo filename
 * @returns {Promise<string>} - Download URL
 */
export const getEquipmentPhotoURL = async (orderId, equipmentId, photoId) => {
  if (!orderId || !equipmentId || !photoId) {
    throw new Error('orderId, equipmentId, y photoId son requeridos');
  }

  try {
    const storagePath = getStoragePath(orderId, equipmentId, photoId);
    const photoRef = ref(storage, storagePath);
    
    const downloadURL = await getDownloadURL(photoRef);
    return downloadURL;
  } catch (error) {
    console.error(`Error getting photo URL for ${photoId}:`, error);
    
    if (error.code === 'storage/object-not-found') {
      throw new Error(`La foto ${photoId} no existe`);
    }
    
    throw new Error(`Error al obtener la URL de la foto ${photoId}: ${error.message}`);
  }
};

/**
 * Lists all photos for a specific equipment
 * @param {string} orderId - Order ID
 * @param {string} equipmentId - Equipment ID
 * @returns {Promise<Array>} - Array of photo metadata
 */
export const listEquipmentPhotos = async (orderId, equipmentId) => {
  if (!orderId || !equipmentId) {
    throw new Error('orderId y equipmentId son requeridos');
  }

  try {
    const folderPath = getStoragePath(orderId, equipmentId);
    const folderRef = ref(storage, folderPath);
    
    const listResult = await listAll(folderRef);
    
    const photoPromises = listResult.items.map(async (itemRef) => {
      try {
        const downloadURL = await getDownloadURL(itemRef);
        return {
          photoId: itemRef.name,
          downloadURL,
          fullPath: itemRef.fullPath
        };
      } catch (error) {
        console.error(`Error getting URL for ${itemRef.name}:`, error);
        return {
          photoId: itemRef.name,
          error: error.message,
          fullPath: itemRef.fullPath
        };
      }
    });

    const photos = await Promise.all(photoPromises);
    return photos;
  } catch (error) {
    console.error(`Error listing photos for equipment ${equipmentId}:`, error);
    throw new Error(`Error al listar las fotos del equipo: ${error.message}`);
  }
};

/**
 * Cleans up all photos for an entire order (when order is deleted)
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} - Cleanup results
 */
export const cleanupOrderPhotos = async (orderId) => {
  if (!orderId) {
    throw new Error('orderId es requerido');
  }

  try {
    const orderFolderPath = `work-orders/${orderId}`;
    const orderFolderRef = ref(storage, orderFolderPath);
    
    // List all items in the order folder recursively
    const listResult = await listAll(orderFolderRef);
    
    // Recursively process all subfolders (equipment folders, media folders, etc.)
    const getAllItemsRecursively = async (prefixRef) => {
      const subListResult = await listAll(prefixRef);
      
      let items = [...subListResult.items];
      
      // Recursively process sub-prefixes
      for (const subPrefix of subListResult.prefixes) {
        const subItems = await getAllItemsRecursively(subPrefix);
        items = items.concat(subItems);
      }
      
      return items;
    };
    
    // Process all prefixes recursively
    let allItems = [...listResult.items];
    for (const prefixRef of listResult.prefixes) {
      const subItems = await getAllItemsRecursively(prefixRef);
      allItems = allItems.concat(subItems);
    }
    
    if (allItems.length === 0) {
      return {
        success: true,
        deletedCount: 0,
        message: 'No se encontraron fotos para eliminar en la orden'
      };
    }

    // Delete all files
    const deletePromises = allItems.map(async (itemRef) => {
      try {
        await deleteObject(itemRef);
        return { success: true, filename: itemRef.name, path: itemRef.fullPath };
      } catch (error) {
        console.error(`Error deleting ${itemRef.fullPath}:`, error);
        return { success: false, filename: itemRef.name, path: itemRef.fullPath, error: error.message };
      }
    });

    const results = await Promise.allSettled(deletePromises);
    const deletedCount = results.filter(result => 
      result.status === 'fulfilled' && result.value.success
    ).length;

    return {
      success: true,
      deletedCount,
      totalFiles: allItems.length,
      results: results.map(result => 
        result.status === 'fulfilled' ? result.value : { success: false, error: result.reason }
      )
    };
  } catch (error) {
    console.error(`Error cleaning up photos for order ${orderId}:`, error);
    throw new Error(`Error al limpiar las fotos de la orden: ${error.message}`);
  }
};

// Export all constants for external use
export const PHOTO_CONSTANTS = {
  SUPPORTED_FORMATS,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB: MAX_FILE_SIZE / (1024 * 1024),
  MAX_VIDEO_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE_MB: MAX_VIDEO_FILE_SIZE / (1024 * 1024)
};